/**
 * DeleteStoreModal — confirmación de eliminación de tienda.
 *
 * Pattern Silicon Valley (GitHub / Stripe / Linear):
 *  - Banner crítico explicando la consecuencia
 *  - Lista qué se eliminará (productos, mensajes, etc)
 *  - Input que requiere escribir el nombre EXACTO de la tienda
 *  - Botón destructivo deshabilitado hasta match exacto
 *  - Loading state durante el delete
 *  - Auto-switch al siguiente tenant del user (si existe)
 *
 * Roadmap §18 — Multi-tenant per user.
 */

import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, TextField, Banner, BlockStack, Text, List } from '@/lib/polaris';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@smarty/shared';
import { logError } from '@/lib/logger';

interface Props {
  open: boolean;
  tenantId: string;
  tenantName: string;
  onClose: () => void;
}

export function DeleteStoreModal({ open, tenantId, tenantName, onClose }: Props) {
  const { switchTenant, signOut, refreshTenants } = useAuth();
  const navigate = useNavigate();
  const [confirmation, setConfirmation] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset al cerrar
  useEffect(() => {
    if (!open) {
      setConfirmation('');
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  const isMatch = confirmation === tenantName;

  const handleDelete = useCallback(async () => {
    if (!isMatch || submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const { data, error: invokeError } = await supabase.functions.invoke<{
        deleted?: boolean;
        next_tenant_id?: string | null;
        error?: string;
        details?: string;
      }>('delete-store', {
        body: { tenant_id: tenantId, confirmation_name: confirmation },
      });

      if (invokeError) {
        const msg = (data?.details as string | undefined) ?? invokeError.message;
        throw new Error(msg);
      }
      if (!data?.deleted) {
        throw new Error(data?.error ?? 'Respuesta inválida del servidor');
      }

      // Telemetría
      try {
        const w = window as unknown as { analytics?: { track: (e: string, p: Record<string, unknown>) => void } };
        w.analytics?.track('tenant_deleted', {
          tenant_id: tenantId,
          tenant_name: tenantName,
          had_other_tenants: !!data.next_tenant_id,
        });
      } catch { /* ignore */ }

      // Invalidar el cache module-level de tenants ANTES del switch para que
      // el dropdown refleje la lista actualizada (sin la tienda eliminada).
      await refreshTenants();

      // Auto-switch al siguiente tenant si existe; si no, signOut
      if (data.next_tenant_id) {
        await switchTenant(data.next_tenant_id);
        navigate('/?tenant_deleted=1', { replace: true });
      } else {
        // Sin más tenants → cerrar sesión y volver a login
        await signOut();
        navigate('/login?tenant_deleted=1', { replace: true });
      }
    } catch (err) {
      logError('[DeleteStoreModal] Error eliminando tienda', err);
      const msg = (err as { message?: string })?.message ?? 'No se pudo eliminar la tienda';
      setError(msg);
      setSubmitting(false);
    }
  }, [isMatch, submitting, tenantId, tenantName, confirmation, switchTenant, signOut, refreshTenants, navigate]);

  return (
    <Modal
      open={open}
      onClose={submitting ? () => {} : onClose}
      title={`Eliminar "${tenantName}"`}
      primaryAction={{
        content: submitting ? 'Eliminando…' : 'Eliminar permanentemente',
        destructive: true,
        loading: submitting,
        disabled: !isMatch || submitting,
        onAction: handleDelete,
      }}
      secondaryActions={[
        {
          content: 'Cancelar',
          disabled: submitting,
          onAction: onClose,
        },
      ]}
    >
      <Modal.Section>
        <BlockStack gap="400">
          <Banner tone="critical" title="Esta acción es irreversible">
            <Text as="p" variant="bodyMd">
              Eliminar esta tienda borrará permanentemente todos sus datos. Esta acción <strong>no se puede deshacer</strong>.
            </Text>
          </Banner>

          <BlockStack gap="200">
            <Text as="p" variant="bodyMd" fontWeight="semibold">
              Se eliminarán permanentemente:
            </Text>
            <List type="bullet">
              <List.Item>Todos los productos, pedidos y clientes</List.Item>
              <List.Item>Conversaciones y mensajes de WhatsApp</List.Item>
              <List.Item>Configuración de WhatsApp y números conectados</List.Item>
              <List.Item>Campañas, automatizaciones y plantillas</List.Item>
              <List.Item>Empleados, sucursales y roles</List.Item>
              <List.Item>Tema, dominio y storefront público</List.Item>
              <List.Item>Históricos, reportes y analytics</List.Item>
            </List>
          </BlockStack>

          {error && (
            <Banner tone="critical">
              <Text as="p">{error}</Text>
            </Banner>
          )}

          <TextField
            label={
              <span>
                Para confirmar, escribe <strong>{tenantName}</strong> exactamente:
              </span>
            }
            value={confirmation}
            onChange={setConfirmation}
            autoComplete="off"
            disabled={submitting}
            placeholder={tenantName}
            error={confirmation.length > 0 && !isMatch ? 'El nombre no coincide' : undefined}
          />
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}
